import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero, Section, FeatureGrid, CTA, Breadcrumbs, Prose, FAQGrid } from "@/components/site/Sections";

const TITLE = "Elektrikerydelser i København | Elektriker-Elektriker";
const DESCRIPTION =
  "Se alle vores elektrikerydelser: ladestandere, varmepumper, energioptimering, fejlfinding, grøn energi og bæredygtighed. Autoriseret elektriker i København & omegn.";

export const Route = createFileRoute("/ydelser")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "/ydelser" },
    ],
    links: [{ rel: "canonical", href: "https://elektriker-elektriker.dk/ydelser" }],
  }),
  component: YdelserPage,
});

const items = [
  { to: "/ladestandere", title: "Ladestandere", body: "Installation af ladeboks til elbil, privat, ejerforening og erhverv. Vi rådgiver om effekt, lastbalancering og tilskud." },
  { to: "/varmepumper", title: "Varmepumper", body: "Luft-til-vand og luft-til-luft varmepumper, uvildig rådgivning, autoriseret installation og hjælp til tilskud." },
  { to: "/energioptimering", title: "Energioptimering", body: "Vi finder besparelserne i din bolig eller virksomhed, typisk 15-35% lavere regning." },
  { to: "/groen-energi", title: "Grøn energi", body: "Solceller, batterier og smart energistyring, producér og lagr din egen strøm." },
  { to: "/fejlfinding", title: "Fejlfinding", body: "Springer din HPFI? Systematisk fejlfinding og dokumenteret el-tjek, døgnvagt 24/7." },
  { to: "/baeredygtighed", title: "Bæredygtighed", body: "Sådan arbejder vi grønt, elbil-flåde, sortering og rådgivning der sænker dit CO2-aftryk." },
];

function YdelserPage() {
  return (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "Ydelser" }]} />
      <PageHero
        variant="stats"
        eyebrow="Ydelser"
        title="Alt indenfor el, ét sted"
        intro="Vi løser alle typer elektrikeropgaver i København og Storkøbenhavn: akut udrykning, planlagte installationer, energiløsninger og total el-entreprise. Én autoriseret installatør, fra første telefonopkald til endelig dokumentation."
        stats={[
          { value: "6", label: "Kerne­ydelser" },
          { value: "24+", label: "Byer & kvarterer" },
          { value: "Garanti", label: "På alt arbejde" },
          { value: "AAA", label: "Kreditværdighed" },
        ]}
      />

      <Section>
        <Prose>
          <h2>Hvad laver en moderne elektriker i 2026?</h2>
          <p>
            En elektriker laver mere end at trække kabler. Vi rådgiver om hele bygningens
            energi­strategi, vi installerer ladestandere og varmepumper, vi designer
            sol- og batterianlæg, vi optimerer forbruget time for time efter elprisen, og vi
            er ofte den, der binder de andre fagentrepriser sammen, når dit hjem skal renoveres.
            Hos os får du en enkelt kontaktperson gennem hele opgaven, uanset om det er en
            enkelt stikkontakt eller en total el-entreprise.
          </p>
          <p>
            Klik dig videre til den ydelse du har brug for, eller ring til os og lad os vurdere
            sammen, vi spilder ikke din tid med tilbud på ting, du ikke har brug for.
          </p>
        </Prose>
      </Section>

      <Section className="!pt-0">
        <FeatureGrid items={items} />
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {items.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              className="rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-[var(--ink)] transition hover:border-primary/50 hover:bg-accent"
            >
              {i.title} →
            </Link>
          ))}
        </div>
      </Section>

      <Section className="!pt-0">
        <h2 className="text-3xl">Hvad vi laver, udover de seks kerneydelser</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {[
            { t: "Akut elektriker / døgnvagt 24/7", b: "HPFI der slår fra, strømsvigt eller brændt lugt, vi rykker ud i hele Storkøbenhavn." },
            { t: "El-renovering af bolig", b: "Total el-renovering ved køkken, badeværelse, tilbygning eller totalrenovering." },
            { t: "Eltavle & gruppefordeling", b: "Udskiftning af gamle tavler, HPFI-opgradering og dokumenteret gruppefordeling." },
            { t: "Belysning & lysdesign", b: "Spots, sensorer, scenestyring, udendørs- og facadebelysning i hjem og virksomheder." },
            { t: "Smart-home & KNX", b: "Installation og konfiguration af KNX-baserede smart-home-løsninger." },
            { t: "Data, net & WiFi-mesh", b: "Strukturerede netværk, WiFi-mesh og POE-kameraer." },
            { t: "Erhvervsservice & SLA", b: "Serviceaftaler med faste timepriser, dokumentation og 24/7 udrykning." },
            { t: "Foreninger", b: "Ejer- og andelsforeninger, fællesanlæg, fælleshus, ladeløsninger og budgetlagte projekter." },
          ].map((s) => (
            <article key={s.t} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-semibold text-[var(--ink)]">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.b}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section className="!pt-0">
        <h2 className="text-3xl">Ofte stillede spørgsmål om vores ydelser</h2>
        <div className="mt-8">
          <FAQGrid items={[
            { q: "Laver I både private og erhverv?", a: "Ja, ca. halvdelen af vores omsætning er privatkunder, og halvdelen er erhverv og foreninger. Vi har timepriser og serviceaftaler til begge segmenter." },
            { q: "Dækker I hele Storkøbenhavn?", a: "Ja, vi kører dagligt i hele Storkøbenhavn, fra Indre By til Nordsjælland og hele Amager. Vi servicerer også opgaver længere væk efter aftale." },
            { q: "Kan jeg få fast pris?", a: "På planlagte opgaver giver vi skriftligt fastpristilbud efter besigtigelse. Ved fejlfinding og service afregner vi i tid plus materialer, med prisen oplyst undervejs." },
            { q: "Hvad er forskellen på akut og planlagt?", a: "Akut udrykning er når noget skal ske med det samme, typisk inden for få timer. Planlagte opgaver bookes 1-3 uger frem og koster mindre. Begge dele har garanti." },
          ]} />
        </div>
      </Section>

      <CTA />
      <div className="h-10" />
    </SiteShell>
  );
}
